import NoDataCard from '@/components/NoDataCard';
import { GroupTreatmentConfiguration } from '@/components/Studies/StudyGroups/Configuration';
import { _isEmpty } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { DraftFormData } from '@/model/Draft.model';
import type { Treatment } from '@/model/Treatment.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import React, { Dispatch, useMemo } from 'react';
import { useStudyCreateGroups } from '@/utils/studyCreation/useStudyCreate.tsx';

interface GroupAndTreatmentConfigurationProps {
  study: DraftFormData;
  dispatch: Dispatch<unknown>;
}

export const GroupAndTreatmentConfiguration: React.FC<GroupAndTreatmentConfigurationProps> = ({ study, dispatch }) => {
  const { groups, treatments, groupsTreatments, updateStudyGroups } = useStudyCreateGroups();

  const treatmentsForConfiguration: Array<Treatment> = useMemo(() => {
    return treatments
      .filter((treatment) => treatment.treatment_classification === 'treatment')
      .map((treatment) => ({
        ...treatment,
        id: treatment?.temp_id ?? '',
      }));
  }, [treatments]);

  const groupsForConfiguration: Array<TreatmentGroup> = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        id: group?.temp_id ?? '',
      })),
    [groups]
  );

  const groupsWithTreatments: Record<ID, Array<ID>> = useMemo(
    () => groupsTreatments.reduce((acc, { group, treatments }) => ({ ...acc, [group]: treatments }), {}),
    [groupsTreatments]
  );

  const handleTreatmentClick = (groupTreatments: Record<ID, Array<ID>>) => {
    const newGroupsTreatments = Object.entries(groupTreatments).map(([group, treatments]) => ({ group, treatments }));
    updateStudyGroups({ type: 'UPDATE_GROUPS_TREATMENTS', groupsTreatments: newGroupsTreatments });
  };

  return _isEmpty(groupsForConfiguration) ? (
    <NoDataCard className="mv4" title="This study has no groups yet" text="You can create groups in the Groups tab" />
  ) : (
    <div className="mw9 center ph3">
      <GroupTreatmentConfiguration
        treatments={treatmentsForConfiguration}
        groups={groupsForConfiguration}
        groupsWithTreatments={groupsWithTreatments}
        onToggleTreatment={handleTreatmentClick}
      />
    </div>
  );
};
